import { toThousandthsSalePrice } from "./localHelpers";
import { CATEGORY_IDS } from "./categoryPriority";

/** Per spec: if neighborhood name exceeds 21 characters, use this phrase in copy. */
export const MAX_NEIGHBORHOOD_CHARS = 21;

export function neighborhoodLabel(rawPlace) {
  const s = rawPlace ? String(rawPlace).trim() : "";
  if (!s || s.length > MAX_NEIGHBORHOOD_CHARS) return "your neighborhood";
  return s;
}

/** When copy uses the fallback label before a noun (e.g. "… your neighborhood's sellers"). */
export function neighborhoodPossessive(rawPlace) {
  const nbh = neighborhoodLabel(rawPlace);
  return nbh === "your neighborhood" ? "your neighborhood's" : nbh;
}

function priceWord(useMedian) {
  return useMedian ? "median" : "average";
}

/** "a 5% decrease" vs "an 8% decrease" — article matches vowel sound at start of the spoken figure. */
function indefiniteArticleBeforePositivePercent(absRoundedPct) {
  const n = Math.abs(Math.round(Number(absRoundedPct)));
  const s = String(n);
  if (s.startsWith("8")) return "an";
  if (n === 11 || n === 18) return "an";
  return "a";
}

function categoryWindows(metrics, categoryId) {
  return (
    metrics.sentenceTrendsOldCategoryWindows?.[categoryId] || {
      same6LastYear: {},
    }
  );
}

function sentenceAverageHomePrice(metrics) {
  const nbh = neighborhoodLabel(metrics.rawPlace);
  const pw = priceWord(metrics.useMedian);
  const { periodPhrase, comparisonPhrase } =
    metrics.phrasesBySection.avgSalePrice;
  const { same6LastYear } = categoryWindows(
    metrics,
    CATEGORY_IDS.AVERAGE_HOME_PRICE,
  );
  const price = toThousandthsSalePrice(same6LastYear.avgSalePrice);
  const perc = same6LastYear.avgSalePricePerc;
  const rise = Math.round(perc);
  const absPerc = Math.abs(Math.round(perc));

  if (perc >= 1) {
    return {
      header: "Higher Sale Prices",
      statement: `Strong buyer demand and fewer homes for sale drive prices up, giving sellers more negotiating power. In ${nbh}, the ${pw} sale price is $${price}, an increase of ${rise}% compared to ${comparisonPhrase}.`,
      logic: "",
      benefit: "Seller's",
    };
  }
  if (perc < -5) {
    return {
      header: "Adjusting Home Prices",
      statement: `When home prices decline, buyers are more price-sensitive and/or many homes are priced high. In ${nbh}, prices have decreased by ${absPerc}% compared to ${comparisonPhrase}. Pricing your home accurately is key to attracting strong offers.`,
      logic: "",
      benefit: "",
    };
  }
  return {
    header: "Stable Sale Prices",
    statement: `When prices remain flat, buyer demand and homes for sale are in balance. With stable sale prices, well-priced homes are still achieving strong offers. In ${nbh}, sales prices have remained steady, with an ${pw} sale price of $${price}.`,
    logic: "",
    benefit: "",
  };
}

function sentencePricePerSqft(metrics) {
  const nbh = neighborhoodLabel(metrics.rawPlace);
  const { same6LastYear } = categoryWindows(
    metrics,
    CATEGORY_IDS.PRICE_PER_SQFT,
  );
  const cur = same6LastYear.avgSqFtPrice.toLocaleString();
  const p = Math.abs(Math.round(same6LastYear.avgSqFtPricePerc));
  const comparisonPhrase = same6LastYear.comparisonPhrase;

  const salePriceDownSqftUp =
    metrics.avgSalePricePerc < 0 && same6LastYear.avgSqFtPricePerc >= 1;

  if (salePriceDownSqftUp) {
    return {
      header: "Rising Price Per Square Foot",
      statement: `Higher sale price per square foot can indicate that homes that do sell are well-prepared, well-priced, and in strong demand among buyers. In ${nbh}, the price per square foot is $${cur}, which is ${p}% higher compared to ${comparisonPhrase}, creating a strong opportunity for sellers.`,
      logic: "",
      benefit: "Seller's",
    };
  }

  if (same6LastYear.avgSqFtPricePerc >= 1) {
    return {
      header: "Rising Home Values",
      statement: `Higher sale price per square foot signals strengthening property values, where sellers are well-positioned to capture higher returns.  In ${nbh}, the price per square foot is $${cur}, which is ${p}% higher compared to ${comparisonPhrase}.`,
      logic: "",
      benefit: "Seller's",
    };
  }
  if (
    same6LastYear.avgSqFtPricePerc > -1 &&
    same6LastYear.avgSqFtPricePerc < 1
  ) {
    return {
      header: "Square Footage Pricing Remains Steady",
      statement: `Consistent price per square foot signals a stable market where sellers are well-positioned to receive higher sale prices. In ${nbh}, the average price per square foot is $${cur}, which remains unchanged compared to ${comparisonPhrase}.`,
      logic: "",
      benefit: "",
    };
  }
  if (same6LastYear.avgSqFtPricePerc <= -1) {
    const article = indefiniteArticleBeforePositivePercent(p);
    return {
      header: "Shifting Values",
      statement: `With buyers paying closer attention to value, homes priced accurately have a competitive edge. In ${nbh}, price per square foot has shifted to $${cur}, ${article} ${p}% decrease compared to ${comparisonPhrase}.`,
      logic: "",
      benefit: "",
    };
  }
  return {
    header: "Square Footage Pricing Remains Steady",
    statement: `Consistent price per square foot signals a stable market where sellers are well-positioned to receive higher sale prices. In ${nbh}, the average price per square foot is $${cur}, which remains unchanged compared to ${comparisonPhrase}.`,
    logic: "",
    benefit: "Seller's",
  };
}

function sentencePctReceived(metrics) {
  const nbh = neighborhoodLabel(metrics.rawPlace);
  const nbhPossessive = neighborhoodPossessive(metrics.rawPlace);
  const { same6LastYear } = categoryWindows(metrics, CATEGORY_IDS.PCT_RECEIVED);
  const pr = Math.round(metrics.percReceived);
  const overTheLast = "Over the last 6 months";
  void (same6LastYear.percReceivedPerc !== undefined);

  if (metrics.percReceived >= 99 && metrics.avgSalePricePerc >= 0) {
    return {
      header: "High % Received Of Asking",
      statement: `When homes sell at or above asking, it shows list pricing is aligned with market demand. Over the past 6 months, ${nbhPossessive} sellers have received ${pr}% of their asking price, clearly demonstrating the value of accurate pricing.`,
      logic: "",
      benefit: "Seller's",
    };
  }
  if (metrics.percReceived >= 99 && metrics.avgSalePricePerc < 0) {
    return {
      header: "",
      statement: "",
      logic: "",
      benefit: "",
    };
  }
  return {
    header: "Offers Under The Asking Price",
    statement: `When homes sell below the asking price, it usually means buyers are being more selective or prices are set too high. Accurately priced homes help protect the home’s value and sell closer to the asking price. In ${nbh}, sellers are receiving ${pr}% of their asking price.`,
    logic: "",
    benefit: "",
  };
}

function sentenceAvgDom(metrics) {
  const nbh = neighborhoodLabel(metrics.rawPlace);
  const nbhPossessive = neighborhoodPossessive(metrics.rawPlace);
  const { same6LastYear } = categoryWindows(metrics, CATEGORY_IDS.AVG_DOM);
  const { comparisonPhrase } = same6LastYear;
  const days = Math.round(same6LastYear.avgDom);
  const daysRolling = Math.round(metrics.avgDom);
  const p = Math.abs(Math.round(same6LastYear.avgDomPerc));

  /** Rolling 6‑Months Trending DOM (`metrics.avgDom`); excludes “Fewer Days to Sell”. */
  if (metrics.avgDom <= 15) {
    return {
      header: "HOMES ARE SELLING QUICKLY",
      statement: `In ${nbh}, homes have sold in an average of ${daysRolling} days over the past 6 months. When homes sell this quickly, it signals a competitive buyer market and creates a strong advantage for sellers.`,
      logic: "",
      benefit: "Seller's",
    };
  }

  if (same6LastYear.avgDomPerc <= -5) {
    return {
      header: "Fewer Days to Sell",
      statement: `When homes sell faster, it creates a strong advantage for sellers. Buyers become more competitive, often leading to stronger offers. In ${nbh}, homes are selling in ${days} days, ${p}% faster than ${comparisonPhrase}.`,
      logic: "",
      benefit: "Seller's",
    };
  }
  if (Math.abs(same6LastYear.avgDomPerc) <= 5) {
    return {
      header: "Days To Sell — Stable",
      statement: `In ${
        nbh === "your neighborhood" ? `${nbhPossessive} homes` : `${nbh}, homes`
      } are selling at a relatively consistent pace with an average ${days} days to sell compared to ${comparisonPhrase}. Well-priced homes continue to sell quickly, while overpriced homes typically sit longer on the market.`,
      logic: "",
      benefit: "",
    };
  }
  return {
    header: "Homes Taking Longer To Sell",
    statement: `Longer days on market can reflect that pricing is too high. Homes that are priced and marketed strategically continue to sell faster. In ${nbh}, the average days on market has increased to ${days} days, which is ${p}% higher compared to ${comparisonPhrase}.`,
    logic: "",
    benefit: "",
  };
}

function sentenceInventory(metrics) {
  const nbh = neighborhoodLabel(metrics.rawPlace);
  const nbhPossessive = neighborhoodPossessive(metrics.rawPlace);
  const { same6LastYear } = categoryWindows(metrics, CATEGORY_IDS.INVENTORY);
  const n = metrics.totalActiveListings;
  void (same6LastYear.inventoryPerc !== undefined);

  if (metrics.inventoryTier === "Low") {
    const inventoryCountPhrase =
      Number(n) === 1
        ? `there is ${n} home for sale`
        : `there are ${n} homes for sale`;
    return {
      header: "Limited Homes For Sale",
      statement: `Fewer homes on the market limit buyers' choices and increase competition. In this type of market, sellers are positioned to capture strong buyer attention. In ${nbh}, ${inventoryCountPhrase}, which is considered LOW inventory.`,
      logic: "",
      benefit: "Seller's",
    };
  }
  if (metrics.inventoryTier === "Balanced") {
    return {
      header: "Balanced Market Conditions",
      statement: `When the number of homes for sale is approximately equal to the number of serious buyers, this creates favorable market conditions where sellers can expect strong and consistent buyer activity. ${nbhPossessive} inventory is currently balanced.`,
      logic: "",
      benefit: "Seller's",
    };
  }
  return {
    header: "More Homes For Sale",
    statement: `A higher number of homes on the market gives buyers more choices and increases competition among sellers. Accurate pricing and presentation are key to attracting buyer attention. In ${nbh}, there are ${n} homes for sale, reflecting higher inventory levels.`,
    logic: "",
    benefit: "",
  };
}

function sentenceUnitsSold(metrics) {
  const nbh = neighborhoodLabel(metrics.rawPlace);
  const { same6LastYear } = categoryWindows(metrics, CATEGORY_IDS.UNITS_SOLD);
  const { comparisonPhrase } = same6LastYear;
  const p = Math.abs(Math.round(same6LastYear.unitsSoldPerc));

  if (same6LastYear.unitsSoldPerc >= 5) {
    return {
      header: "Increased Market Activity",
      statement: `Increased home sales signal a healthy assortment of homes and active buyer demand. In ${nbh}, ${p}% more homes have sold compared to ${comparisonPhrase}—giving sellers an advantage from strong market momentum.`,
      logic: "",
      benefit: "Seller's",
    };
  }
  if (same6LastYear.unitsSoldPerc < 0) {
    return {
      header: "Decreased Market Activity",
      statement: `Reduced unit sales often reflect limited inventory or more cautious buyers. In ${nbh}, sales are down ${p}% compared to the same period last year, suggesting prices may be higher than what today's buyers are willing to pay—making accurate pricing especially important.`,
      logic: "",
      benefit: "",
    };
  }
  return {
    header: "Increased Market Activity",
    statement: `Unit sales have held relatively steady compared to ${comparisonPhrase}. In ${nbh}, closed sales are flat with only a small change versus the prior period.`,
    logic: "",
    benefit: "",
  };
}

const builders = {
  [CATEGORY_IDS.AVERAGE_HOME_PRICE]: sentenceAverageHomePrice,
  [CATEGORY_IDS.PRICE_PER_SQFT]: sentencePricePerSqft,
  [CATEGORY_IDS.PCT_RECEIVED]: sentencePctReceived,
  [CATEGORY_IDS.UNITS_SOLD]: sentenceUnitsSold,
  [CATEGORY_IDS.AVG_DOM]: sentenceAvgDom,
  [CATEGORY_IDS.INVENTORY]: sentenceInventory,
};

export function buildSentencesForCategories(categoryIds, metrics) {
  return categoryIds.map((id) => {
    const fn = builders[id];
    if (!fn) {
      return {
        header: "",
        statement: "",
        logic: "",
        benefit: "",
        categoryId: id,
      };
    }
    return { ...fn(metrics), categoryId: id };
  });
}
